import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProjectCard from './ProjectCard';
import ProjectViewDrawer from './ProjectViewDrawer';
import ProjectImport from './ProjectImport';
import ProjectExport from './ProjectExport';
import ProjectAPIs from '../../utilities/api/project/AdminProjectAPI';
import AdminUserList from '../../utilities/api/user/AdminUserList';
import { useSelector } from 'react-redux';
import nodatafound from '../../utilities/dummy_assets/nodatafound.jpg';
import { toast } from 'react-toastify';

const progressOptions = {
  1: 'Archived',
  2: 'In Progress',
  3: 'Completed',
};

const typeOptions = {
  1: 'Academic',
  2: 'Research',
  3: 'Personal',
  4: 'Other',
};

const Projectscont = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [allUsersData, setAllUsersData] = useState([]);

  const user = useSelector(state => state.user.user);

  useEffect(() => {
    const fetchProjectsAndUsers = async () => {
      setIsLoading(true);
      try {
        const [projectData, userData] = await Promise.all([
          ProjectAPIs.ProjectsGet(),
          AdminUserList.AllUserListGet(),
        ]);
        setProjects(projectData);
        setFilteredProjects(projectData);
        setUsers(userData);
        setAllUsersData(userData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch initial project or user data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectsAndUsers();
  }, [user]);

  useEffect(() => {
    let filtered = projects;
    if (typeFilter) {
      filtered = filtered.filter(
        project => project.type.toString() === typeFilter
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(
        project => project.progress_status.toString() === statusFilter
      );
    }
    if (searchTerm) {
      filtered = filtered.filter(
        project =>
          project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredProjects(filtered);
  }, [typeFilter, statusFilter, searchTerm, projects]);

  const handleViewProject = project => {
    setSelectedProject(project);
    setDrawerOpen(true);
  };

  const toggleDrawer = open => {
    setDrawerOpen(open);
  };

  const getUserDetails = userId => users.find(user => user.id === userId);

  const handleImportComplete = async () => {
    try {
      // Refresh project list after import
      const projectData = await ProjectAPIs.ProjectsGet();
      setProjects(projectData);
      setFilteredProjects(projectData);
    } catch (error) {
      console.error('Error refreshing projects:', error);
      toast.error('Failed to refresh projects after import.');
    }
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 2 }}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, px: 1 }}
      >
        {/* TOP ROW: Type | Status | Export | Import */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {/* Type */}
          <FormControl
            size="small"
            sx={{
              minWidth: isMobile ? 120 : 200,
              flex: isMobile ? '1 1 auto' : 'unset',
            }}
          >
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              label="Type"
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {Object.entries(typeOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl
            size="small"
            sx={{
              minWidth: isMobile ? 120 : 200,
              flex: isMobile ? '1 1 auto' : 'unset',
            }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              label="Status"
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {Object.entries(progressOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Export */}
          <Box sx={{ flexShrink: 0 }}>
            <ProjectExport projects={filteredProjects} users={users} />
          </Box>

          {/* Import */}
          <Box sx={{ flexShrink: 0 }}>
            <ProjectImport
              user={user}
              allUsersData={allUsersData}
              onImportComplete={handleImportComplete}
            />
          </Box>
           {/* SEARCH BELOW */}
        <TextField
          fullWidth
          placeholder="Search..."
          variant="standard"
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            setSearchTerm(e.target.value);
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fill: '#666666' }} />,
            sx: {
              background: '#FAFAFA',
              border: '1px solid #E6E6E6',
              borderRadius: '8px',
              padding: '4px 16px',
            },
          }}
          sx={{
            marginLeft: {md: 0, lg: 'auto'},
            width: 'auto',
            flex: 1,
            minWidth: '200px',
            maxWidth: '330px',
            '.MuiOutlinedInput-root': {
              borderRadius: '12px',
            },
            '.MuiInput-root:before, .MuiInput-root:after': {
              border: 'none !important'
            }
          }}
        />
        </Box>

       
      </Box>
      <Grid container spacing={2}>
        {isLoading ? (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Grid>
        ) : filteredProjects.length > 0 ? (
          filteredProjects.map(project => (
            <Grid item key={project.id} xs={12} sm={6} md={4}>
              <ProjectCard
                project={project}
                onView={handleViewProject}
                getUserDetails={getUserDetails}
                allUser={users}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12} sx={{ textAlign: 'center', padding: 4, mt: 8 }}>
            <img
              src={nodatafound}
              alt="No data found"
              style={{ width: '350px', marginBottom: '16px' }}
            />
            <Typography variant="h6">No Projects Found</Typography>
          </Grid>
        )}
      </Grid>
      <ProjectViewDrawer
        drawerWidth={400}
        drawerOpen={drawerOpen}
        toggleDrawer={toggleDrawer}
        currentProject={selectedProject}
        getUserDetails={getUserDetails}
      />
    </Box>
  );
};

export default Projectscont;
